import { Banner, Button, EmptyState, Select, Spinner, TextField } from '@ui';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { EPIC_BOARD, EPIC_CLOSING_STATUS, EPIC_RESOLUTION_TITLES, EPIC_RESOLUTIONS, epicMoveAllowed, type EpicResolution } from '../../../shared/board';
import { bugApi, type Epic } from '../bugApi';
import { RefreshIcon, SearchIcon } from '../icons';
import { EpicCard } from './EpicBoard';

// Upstream board (Ап1–Ап5, model quint/specs/epic_process.qnt): a column per epic stage, the resolution lanes on Delivery Done.
// The view classes are the ones of the report board (the view may be shared, the logic is not): here a card goes to any stage,
// and Delivery Done asks for the resolution first.

type OnError = (err: unknown, fallback: string) => void;

const errorText = (err: unknown, fallback: string) => (err instanceof Error && err.message ? err.message : fallback);
const RESOLUTION_OPTIONS = EPIC_RESOLUTIONS.map((value) => ({ value, label: EPIC_RESOLUTION_TITLES[value] }));

/** Ап5: each epic in exactly one column of its stage; in Delivery Done, in the lane of its resolution. */
export function epicLanes(epics: Epic[], status: string): { resolution: EpicResolution; cards: Epic[] }[] {
  return EPIC_RESOLUTIONS.map((resolution) => ({ resolution, cards: epics.filter((e) => e.status === status && e.resolution === resolution) }));
}

export default function EpicKanban({ onError }: { onError: OnError }) {
  const [epics, setEpics] = useState<Epic[] | null>(null);
  const [text, setText] = useState('');
  const [dragging, setDragging] = useState<Epic | null>(null);
  const [pending, setPending] = useState<Set<number>>(new Set());
  const [problem, setProblem] = useState('');
  const [ask, setAsk] = useState<{ epic: Epic; resolution: string } | null>(null);
  const [openId, setOpenId] = useState<number | null>(null);

  const load = useCallback(async () => {
    try {
      setEpics((await bugApi.epics()).epics);
    } catch (err) {
      onError(err, 'Не удалось загрузить доску эпиков');
    }
  }, [onError]);

  useEffect(() => {
    void load();
  }, [load]);

  const put = useCallback((epic: Epic) => setEpics((prev) => (prev ?? []).map((e) => (e.id === epic.id ? epic : e))), []);

  /** The move carries the stage seen; on any failure the card stays where the epic really is. */
  const send = async (epic: Epic, status: string, resolution?: string) => {
    setProblem('');
    setPending((prev) => new Set(prev).add(epic.id));
    try {
      put((await bugApi.updateEpic(epic.id, { status, ...(resolution ? { resolution } : {}), expectedStatus: epic.status })).epic);
    } catch (err) {
      setProblem(`◆ ${epic.id}: ${errorText(err, 'не удалось перенести')}`);
      try {
        put((await bugApi.epic(epic.id)).epic);
      } catch {
        /* the card stays */
      }
    } finally {
      setPending((prev) => {
        const next = new Set(prev);
        next.delete(epic.id);
        return next;
      });
    }
  };

  /** Ап2: a drop into Delivery Done asks for the resolution; a drop onto a lane of it carries that resolution. */
  const drop = (status: string, resolution?: string) => {
    const epic = dragging;
    setDragging(null);
    if (!epic || (status === epic.status && (status !== EPIC_CLOSING_STATUS || !resolution || resolution === epic.resolution))) return;
    if (status !== epic.status && !epicMoveAllowed(epic.status, status)) return;
    if (status === EPIC_CLOSING_STATUS) {
      if (resolution) void send(epic, status, resolution);
      else setAsk({ epic, resolution: '' });
    } else void send(epic, status);
  };

  const shown = useMemo(() => {
    const q = text.trim().toLowerCase();
    return (epics ?? []).filter((e) => !q || [`№${e.id}`, e.title, e.description].some((v) => v.toLowerCase().includes(q)));
  }, [epics, text]);

  if (!epics) return <Spinner label="Загрузка доски эпиков" />;

  const card = (e: Epic) => (
    <button
      key={e.id}
      type="button"
      className={`board-card${pending.has(e.id) ? ' is-pending' : ''}${openId === e.id ? ' is-open' : ''}`}
      draggable={!pending.has(e.id)}
      onDragStart={(ev) => {
        ev.dataTransfer?.setData('text/plain', String(e.id));
        setDragging(e);
      }}
      onDragEnd={() => setDragging(null)}
      onClick={() => setOpenId(openId === e.id ? null : e.id)}
      aria-busy={pending.has(e.id)}
    >
      <span className="board-card-top">
        <span className="mono" data-bug-open="">
          ◆ {e.id}
        </span>
      </span>
      <span className="board-card-text" data-pii="">
        {e.title}
      </span>
      <span className="board-card-meta" data-bug-open="">
        {e.progress.total ? `${e.progress.done} / ${e.progress.total}` : 'нет репортов'}
      </span>
    </button>
  );

  return (
    <div className="board">
      <div className="board-toolbar">
        <TextField
          label="Поиск по доске эпиков"
          hideLabel
          size="sm"
          type="search"
          icon={<SearchIcon />}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onClear={text ? () => setText('') : undefined}
          placeholder="Номер, название"
          className="board-search"
        />
        <Button size="sm" variant="quiet" icon={<RefreshIcon />} onClick={() => void load()}>
          Обновить
        </Button>
      </div>
      {problem && (
        <div role="alert" className="board-problem">
          <Banner tone="error" onClose={() => setProblem('')} closeLabel="Скрыть">
            {problem}
          </Banner>
        </div>
      )}
      {ask && (
        <form
          className="board-dup"
          aria-label="Резолюция эпика"
          onSubmit={(e) => {
            e.preventDefault();
            if (!ask.resolution) return;
            void send(ask.epic, EPIC_CLOSING_STATUS, ask.resolution);
            setAsk(null);
          }}
        >
          <span>◆ {ask.epic.id} → Delivery Done, резолюция:</span>
          <Select
            label="Резолюция"
            hideLabel
            size="sm"
            placeholder="Резолюция…"
            value={ask.resolution}
            onChange={(value) => setAsk({ ...ask, resolution: value })}
            options={RESOLUTION_OPTIONS}
          />
          <Button type="submit" size="sm" variant="primary" disabled={!ask.resolution}>
            Перенести
          </Button>
          <Button type="button" size="sm" variant="quiet" onClick={() => setAsk(null)}>
            Отмена
          </Button>
        </form>
      )}
      {epics.length === 0 && <EmptyState compact>Эпиков нет</EmptyState>}
      <div className="board-scroll">
        <div className="board-lane">
          <div className="board-columns" style={{ gridTemplateColumns: `repeat(${EPIC_BOARD.columns.length}, minmax(180px, 1fr))` }}>
            {EPIC_BOARD.columns.map((column) => {
              const cards = shown.filter((e) => e.status === column.id);
              const target = dragging ? column.id !== dragging.status && epicMoveAllowed(dragging.status, column.id) : null;
              return (
                <section
                  key={column.id}
                  className={`board-column${target === false ? ' is-blocked' : ''}${target ? ' is-target' : ''}`}
                  aria-label={column.title}
                  onDragOver={(e) => {
                    if (dragging) e.preventDefault();
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    drop(column.id);
                  }}
                >
                  <header className="board-column-head" data-bug-open="">
                    <span>{column.title}</span>
                    <span className="board-count">{epics.filter((e) => e.status === column.id).length}</span>
                  </header>
                  {column.id === EPIC_CLOSING_STATUS
                    ? epicLanes(cards, column.id).map((lane) =>
                        lane.cards.length > 0 || dragging ? (
                          <div
                            key={lane.resolution}
                            className={`board-resolution${dragging ? ' is-target' : ''}`}
                            data-resolution={lane.resolution}
                            onDragOver={(e) => {
                              if (dragging) e.preventDefault();
                            }}
                            onDrop={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              drop(column.id, lane.resolution);
                            }}
                          >
                            <div className="board-resolution-head" data-bug-open="">
                              <span>{EPIC_RESOLUTION_TITLES[lane.resolution]}</span>
                              <span className="board-count">{lane.cards.length}</span>
                            </div>
                            {lane.cards.map(card)}
                          </div>
                        ) : null,
                      )
                    : cards.map(card)}
                </section>
              );
            })}
          </div>
        </div>
      </div>
      {openId !== null && (
        <EpicCard
          id={openId}
          onChanged={put}
          onDeleted={() => {
            setEpics((prev) => (prev ?? []).filter((e) => e.id !== openId));
            setOpenId(null);
          }}
          onError={onError}
        />
      )}
    </div>
  );
}
